import {
  buildMultiTravelerBookingForm,
  type BuildMultiTravelerCheckoutSubmitArgs,
} from './checkout_submit.js';
import {
  conciergeReasonKey,
  type ConciergeRequestState,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import type {
  ConciergeRentalOption,
  ConciergeRequest,
  ConciergeSubmit,
} from '@voll-intelligence/genui-contract';

type Concierge = BuildConciergeSubmitArgs['concierge'];

export interface BuildConciergeSubmitArgs extends Omit<
  BuildMultiTravelerCheckoutSubmitArgs,
  'challengeId' | 'item'
> {
  request: ConciergeRequest;
  concierge: Pick<
    ConciergeRequestState,
    | 'reasons'
    | 'reasonKey'
    | 'details'
    | 'onlyDirect'
    | 'onlyWithLuggage'
    | 'periods'
    | 'preferredHotel'
    | 'bedType'
    | 'busSeat'
    | 'rentalCategories'
    | 'rentalCompanies'
    | 'categoryId'
    | 'companyId'
  >;
}

/** Um id escolhido no cartão contra o catálogo carregado; fora dele derruba o
 *  submit (a mesma régua do motivo do pedido). Vazio = nada escolhido. */
function catalogRef(
  options: readonly ConciergeRentalOption[],
  id: string,
  label: string,
): { id: string; name: string } | undefined {
  if (!id) return undefined;
  const option = options.find((candidate) => candidate.id === id);
  if (!option) throw new Error(`${label} "${id}" não encontrada — reabra o checkout`);
  return { id: option.id, name: option.name };
}

/** Categoria (obrigatória) e locadora do carro, resolvidas contra os
 *  catálogos carregados. */
function rentalAsks(concierge: Concierge): NonNullable<ConciergeSubmit['rental']> {
  const category = catalogRef(concierge.rentalCategories, concierge.categoryId, 'categoria');
  if (!category) throw new Error('pedido de carro sem categoria');
  const company = catalogRef(concierge.rentalCompanies, concierge.companyId, 'locadora');
  return { category, ...(company ? { company } : {}) };
}

/**
 * Form multi-viajante + cartão do pedido → o `ConciergeSubmit`. A metade
 * corporativa sai do MESMO builder do checkout online (fail-closed nos ids que
 * o form não reconhece); o motivo do pedido resolve contra o catálogo
 * carregado, e um id que não está nele derruba o submit em vez de ecoar um
 * rótulo inventado — a mesma régua do `namedRef` de lá.
 */
export function buildConciergeSubmit(args: BuildConciergeSubmitArgs): ConciergeSubmit {
  const form = buildMultiTravelerBookingForm(args);
  const { request, concierge } = args;
  const reason = concierge.reasonKey
    ? concierge.reasons.find((option) => conciergeReasonKey(option) === concierge.reasonKey)
    : undefined;
  if (concierge.reasonKey && !reason) {
    throw new Error(`motivo do pedido "${concierge.reasonKey}" não encontrado — reabra o checkout`);
  }
  const details = concierge.details.trim();
  return {
    productType: request.productType,
    ...form,
    ...(reason ? { reason: { id: reason.id, name: reason.name } } : {}),
    ...(details ? { details } : {}),
    ...productAsks(request, concierge),
  };
}

/** O bloco do produto no submit: o que a pessoa acrescentou ao pedido. */
function productAsks(
  request: ConciergeRequest,
  concierge: Concierge,
): Pick<ConciergeSubmit, 'flight' | 'hotel' | 'bus' | 'rental'> {
  switch (request.productType) {
    case 'flight':
      return {
        flight: {
          onlyDirect: concierge.onlyDirect,
          onlyWithLuggage: concierge.onlyWithLuggage,
          segments: request.segments.map((segment, index) => {
            const period = concierge.periods[index] ?? segment.period;
            return { ...segment, ...(period ? { period } : {}) };
          }),
        },
      };
    case 'hotel':
      return {
        hotel: {
          ...(concierge.preferredHotel.trim()
            ? { preferredHotel: concierge.preferredHotel.trim() }
            : {}),
          ...(concierge.bedType ? { bedType: concierge.bedType } : {}),
        },
      };
    case 'bus':
      return { bus: busAsks(request, concierge) };
    case 'rental':
      return { rental: rentalAsks(concierge) };
    default: {
      const exhaustive: never = request;
      return exhaustive;
    }
  }
}

/** As preferências do ônibus, todas obrigatórias: o `canSubmit` do cartão já
 *  as exige, então uma ausente aqui derruba o submit em vez de enviar um pedido
 *  que o bus-api recusaria. */
function busAsks(
  request: Extract<ConciergeRequest, { productType: 'bus' }>,
  concierge: Concierge,
): NonNullable<ConciergeSubmit['bus']> {
  const [outwardPeriod, returnPeriod] = concierge.periods;
  const { position, rowPosition, seatClass } = concierge.busSeat;
  if (!outwardPeriod || !position || !rowPosition || !seatClass) {
    throw new Error('preferências do ônibus incompletas — reabra o checkout');
  }
  if (request.returnDate && !returnPeriod) {
    throw new Error('período da volta ausente — reabra o checkout');
  }
  return {
    outwardPeriod,
    ...(request.returnDate && returnPeriod ? { returnPeriod } : {}),
    seatPosition: position,
    seatRowPosition: rowPosition,
    seatClass,
  };
}
